import type { ReactNode } from "react"

import type { StatusTier } from "@/components/ui/status-dot"
import {
  Badge,
  type BadgeColor,
  BadgeIcon,
  BadgeWithDot,
} from "@/components/ui-kit/untitled/base/badges/badges"

import { cx } from "@uui/utils/cx"

export { Badge, type BadgeColor, BadgeIcon, BadgeWithDot }
export type { StatusTier }

/*
 * Status as the system says it: a 20px pill on its tier's
 * own tint with the dot kept and the word in Plex Sans 500 at 11px. No red; an
 * error is caution grammar. Inside a sentence or a chip the dot and the word
 * alone (`StatusDot`) still stand.
 *
 *   confirmed  do.800 on do.100 (7.0:1), the do.500 dot: ready, done, delivered
 *   caution    caution.700 on caution.50 (5.35:1) in a caution.500 ring at 35%:
 *              needs a person
 *   system     stone.700 on stone.100 in a stone.300 ring, the dot ink @52%:
 *              settled negatives (cancelled, terminated), sync rows
 *   remote     print.700 on paper with the dashed print.700 edge and the
 *              print.600 pulse: live, active, fetched on view
 *   neutral    a label, not a status: a hairline outline chip with no dot
 */
const TIER: Record<StatusTier, { root: string; dot: string | null }> = {
  confirmed: { root: "bg-do-100 text-do-800", dot: "confirmed-dot" },
  caution: {
    root: "bg-caution-50 text-caution-700 ring-1 ring-caution-500/35 ring-inset",
    dot: "caution-dot",
  },
  system: {
    root: "bg-stone-100 text-stone-700 ring-1 ring-stone-300 ring-inset",
    dot: "system-dot",
  },
  remote: {
    root: "bg-primary text-brand-secondary outline-1 outline-dashed -outline-offset-1 outline-print-700",
    dot: "remote-dot",
  },
  neutral: {
    root: "text-secondary ring-secondary rounded-md ring-1 ring-inset",
    dot: null,
  },
}

export function StatusBadge({
  tier,
  className,
  children,
}: {
  tier: StatusTier
  className?: string
  children: ReactNode
}) {
  const { root, dot } = TIER[tier]
  return (
    <span
      className={cx(
        "inline-flex h-5 items-center gap-[5px] rounded-full pr-2 pl-[7px] font-sans text-[11px] leading-none font-medium whitespace-nowrap",
        root,
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={dot} />}
      {children}
    </span>
  )
}
